import { useCallback, useRef, useState } from 'react'

import { useAccountId } from '~publish/contexts/AccountContext'

import type { NotesReadState } from './useNotesReadState'

export type NotesReadAction = {
  submit: (noteId: string) => Promise<boolean>
  pending: boolean
  error: 'request' | null
}

export function useNotesReadAction(
  postId: string,
  applyState: (state: NotesReadState) => void,
  write: (postId: string, noteId: string) => Promise<NotesReadState | null>,
): NotesReadAction {
  const accountId = useAccountId()
  const key = accountId ? JSON.stringify([accountId, postId]) : null
  const currentKey = useRef(key)
  currentKey.current = key
  const pendingKeys = useRef(new Set<string>())
  const [pending, setPending] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const submit = useCallback(
    async (noteId: string): Promise<boolean> => {
      if (!key || !noteId || pendingKeys.current.has(key)) return false
      pendingKeys.current.add(key)
      setPending(key)
      setError(null)
      let succeeded = false
      try {
        const state = await write(postId, noteId)
        succeeded = state !== null
        if (state && currentKey.current === key) applyState(state)
      } catch {
        succeeded = false
      } finally {
        pendingKeys.current.delete(key)
        setPending((current) => (current === key ? null : current))
      }
      if (!succeeded && currentKey.current === key) setError(key)
      return succeeded
    },
    [applyState, key, postId, write],
  )

  return {
    submit,
    pending: pending === key,
    error: error === key ? 'request' : null,
  }
}
